import {CurrencyPipe, DatePipe, DecimalPipe, NgStyle} from '@angular/common';
import {Component, inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {MetaDimensao, MetaGrupo, MetaProgresso, StatusProjecao, TIPO_META_CONFIG, TipoMetaConfig} from '@core/models/meta';
import {Notificacao} from '@core/models/notificacoes';
import {MetaService} from '@core/services/meta.service';
import {NotificacaoService} from '@core/services/notificacao.service';
import {PageableParams} from '@core/models/pageable-params';
import {environment} from '@environments/environment';
import {ProgressBar} from 'primeng/progressbar';
import {Tag} from 'primeng/tag';
import {SelectButton} from 'primeng/selectbutton';
import {Button} from 'primeng/button';
import {TableModule} from 'primeng/table';
import {ChartModule} from 'primeng/chart';
import {FormsModule} from '@angular/forms';
import {MetaValorPipe} from '@shared/pipes/meta-valor.pipe';

const CORES_CLARO = ['#2a78d6', '#1baf7a', '#eda100', '#008300', '#4a3aa7', '#e34948', '#e87ba4', '#eb6834'];
const CORES_ESCURO = ['#3987e5', '#199e70', '#c98500', '#008300', '#9085e9', '#e66767', '#d55181', '#d95926'];
const MAX_FATIAS = 7;

function hashChave(chave: string): number {
  let hash = 0;
  for (let i = 0; i < chave.length; i++) {
    hash = (hash * 31 + chave.charCodeAt(i)) >>> 0;
  }
  return hash;
}

const formatMoeda = (v: number) => new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL', maximumFractionDigits: 0}).format(v);

@Component({
  selector: 'app-meta-progresso-panel',
  imports: [
    CurrencyPipe, DatePipe, DecimalPipe, NgStyle,
    ProgressBar, Tag, SelectButton, Button, TableModule, ChartModule, FormsModule, MetaValorPipe,
  ],
  templateUrl: './meta-progresso-panel.html',
})
export class MetaProgressoPanel implements OnChanges {
  @Input({required: true}) progresso!: MetaProgresso;

  private metaService = inject(MetaService);
  private notificacaoService = inject(NotificacaoService);

  dimensoes: {label: string; value: MetaDimensao}[] = [
    {label: 'Curso', value: 'curso'},
    {label: 'Concessionária', value: 'concessionaria'},
    {label: 'Status', value: 'status'},
    {label: 'Fase', value: 'fase'},
    {label: 'Motivo', value: 'motivo'},
  ];
  dimensaoSelecionada: MetaDimensao = 'curso';
  chaveSelecionada: string | null = null;

  drillDownResultados: Notificacao[] = [];
  drillDownLoading = false;
  drillDownDesatualizado = false;

  chartData: any;
  chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          padding: 12,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.label}: ${formatMoeda(ctx.parsed)}`,
        },
      },
    },
    onHover: (event: any, elements: any[]) => {
      if (event?.native?.target) (event.native.target as HTMLElement).style.cursor = elements.length ? 'pointer' : 'default';
    },
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['progresso']) {
      if (this.chaveSelecionada) {
        this.drillDownDesatualizado = true;
      }
      this.montarGrafico();
    }
  }

  /** Grupos exibidos no grid de cards: todos, sem limite de "Outros". */
  get grupoAtual(): MetaGrupo[] {
    return this.progresso?.agrupamentos?.[this.dimensaoSelecionada] ?? [];
  }

  /** Grupos exibidos no gráfico: top N + "Outros" agregando o resto, para não virar uma pizza com fatias ilegíveis. */
  private get grupoGrafico(): MetaGrupo[] {
    const grupos = this.grupoAtual;
    if (grupos.length <= MAX_FATIAS) return grupos;
    const principais = grupos.slice(0, MAX_FATIAS);
    const outros = grupos.slice(MAX_FATIAS);
    const outrosSomados: MetaGrupo = {
      chave: 'Outros',
      quantidade: outros.reduce((s, g) => s + g.quantidade, 0),
      valorContribuido: outros.reduce((s, g) => s + g.valorContribuido, 0),
      percentualDaMeta: outros.reduce((s, g) => s + g.percentualDaMeta, 0),
    };
    return [...principais, outrosSomados];
  }

  get tipoConfig(): TipoMetaConfig {
    return TIPO_META_CONFIG[this.progresso.tipoMeta];
  }

  corDe(chave: string): string {
    if (chave === 'Outros') return document.documentElement.classList.contains('dark') ? '#52514e' : '#c3c2b7';
    const escuro = document.documentElement.classList.contains('dark');
    const paleta = escuro ? CORES_ESCURO : CORES_CLARO;
    return paleta[hashChave(chave) % paleta.length];
  }

  selecionarDimensao(dim: MetaDimensao) {
    this.dimensaoSelecionada = dim;
    this.limparFiltro();
    this.montarGrafico();
  }

  selecionarGrupo(chave: string) {
    this.chaveSelecionada = this.chaveSelecionada === chave ? null : chave;
    this.drillDownDesatualizado = false;
    if (this.chaveSelecionada) this.carregarDrillDown();
    else this.drillDownResultados = [];
  }

  recarregarDrillDown() {
    this.drillDownDesatualizado = false;
    this.carregarDrillDown();
  }

  onChartSelect(event: any) {
    const index = event?.element?.index;
    const grupo = this.grupoGrafico[index];
    if (grupo && grupo.chave !== 'Outros') this.selecionarGrupo(grupo.chave);
  }

  limparFiltro() {
    this.chaveSelecionada = null;
    this.drillDownResultados = [];
    this.drillDownDesatualizado = false;
  }

  private carregarDrillDown() {
    if (!this.chaveSelecionada || !this.progresso) return;
    this.drillDownLoading = true;

    if ((environment as any).local) {
      this.metaService.getMockNotificacoesFiltradas(this.progresso, this.dimensaoSelecionada, this.chaveSelecionada).subscribe(resultados => {
        this.drillDownResultados = resultados;
        this.drillDownLoading = false;
      });
      return;
    }

    const filters: Record<string, unknown> = {
      dataNotificacaoInicio: this.progresso.dataInicio,
      dataNotificacaoFim: this.progresso.dataFim,
      status: this.dimensaoSelecionada === 'status'
        ? [this.chaveSelecionada]
        : this.progresso.statusContribuintes,
    };
    if (this.dimensaoSelecionada !== 'status') {
      filters[this.dimensaoSelecionada] = [this.chaveSelecionada];
    }

    const params = new PageableParams({page: 0, size: 20, filters});
    this.notificacaoService.get(params).subscribe({
      next: page => {
        this.drillDownResultados = page.content;
        this.drillDownLoading = false;
      },
      error: () => this.drillDownLoading = false,
    });
  }

  private montarGrafico() {
    const grupos = this.grupoGrafico;
    this.chartData = {
      labels: grupos.map(g => g.chave),
      datasets: [{
        data: grupos.map(g => g.valorContribuido),
        backgroundColor: grupos.map(g => this.corDe(g.chave)),
        borderWidth: 2,
        borderColor: document.documentElement.classList.contains('dark') ? '#000000' : '#ffffff',
      }],
    };
  }

  get corProgresso(): string {
    if (!this.progresso) return '#E41B2E';
    if (this.progresso.percentualAtingido >= 100) return '#16a34a';
    if (this.progresso.percentualAtingido >= 75) return '#2563eb';
    if (this.progresso.percentualAtingido >= 50) return '#d97706';
    return '#E41B2E';
  }

  get labelDiasRestantes(): string {
    if (!this.progresso) return '';
    const d = this.progresso.diasRestantes;
    if (d < 0) return 'Encerrada';
    if (d === 0) return 'Último dia!';
    if (d === 1) return '1 dia restante';
    return `${d} dias restantes`;
  }

  get severidadeDias(): 'success' | 'warn' | 'danger' | 'info' {
    if (!this.progresso) return 'info';
    const d = this.progresso.diasRestantes;
    if (d < 0) return 'danger';
    if (d <= 30) return 'warn';
    return 'success';
  }

  private static readonly PROJECAO_INFO: Record<StatusProjecao, {label: string; icon: string; severity: 'success' | 'info' | 'warn' | 'danger'}> = {
    ATINGIDA: {label: 'Meta já atingida', icon: 'pi pi-check-circle', severity: 'success'},
    NO_CAMINHO: {label: 'No ritmo certo para atingir a meta', icon: 'pi pi-thumbs-up', severity: 'info'},
    FORA_DO_RITMO: {label: 'No ritmo atual, a meta não será atingida', icon: 'pi pi-exclamation-triangle', severity: 'warn'},
    ENCERRADA: {label: 'Período encerrado sem atingir a meta', icon: 'pi pi-times-circle', severity: 'danger'},
  };

  get projecaoInfo() {
    return MetaProgressoPanel.PROJECAO_INFO[this.progresso.projecao!.status];
  }
}
